import useStickyAlerts from '@/modules/alerts/composables/useStickyAlerts';
import StickyAlert from '@/modules/alerts/models/sticky-alert';
import { STICKY_ALERT_TYPE } from '@/modules/alerts/models/sticky-alert-type';
import NotificationStickyAlertProps, {
  NOTIFICATION_STICKY_ALERT_TYPE,
} from '@/modules/alerts/models/notification-sticky-alert-props';
import { isInStandaloneMode } from '@/utils/pwa-utils';

const { addAlert } = useStickyAlerts();
const FIREBASE_VERSION = '12.4.0';
const FIREBASE_APP_SCRIPT = `https://www.gstatic.com/firebasejs/${FIREBASE_VERSION}/firebase-app.js`;
const FIREBASE_MESSAGING_SCRIPT = `https://www.gstatic.com/firebasejs/${FIREBASE_VERSION}/firebase-messaging.js`;
const SERVICE_WORKER_URL_PWA = 'api/push/pwa/sw.js';
const SERVICE_WORKER_URL_NON_PWA = 'api/push/non-pwa/sw.js';
const SERVICE_WORKER_SCOPE = '/';
const ACTIVATION_TIMEOUT = 10000;
const NOTIFICATION_ALERT_TIMEOUT_MS = 86400000;

class FirebaseService {
  private static instance: FirebaseService;

  private scriptsLoaded: boolean = false;

  private scriptLoadingPromise: Promise<void> | null = null;

  private serviceWorkerRegistration: ServiceWorkerRegistration | null = null;

  private firebaseApp: any = null;

  private messaging: any = null;

  private firebaseMessagingModule: any = null;

  public static getInstance(): FirebaseService {
    if (!FirebaseService.instance) {
      FirebaseService.instance = new FirebaseService();
    }
    return FirebaseService.instance;
  }

  private async loadFirebaseScripts(): Promise<void> {
    if (this.scriptsLoaded) {
      console.debug('[Firebase Service] Scripts already loaded, skipping');
      return;
    }

    if (this.scriptLoadingPromise !== null) {
      await this.scriptLoadingPromise;
      return;
    }

    this.scriptLoadingPromise = (async () => {
      const [firebaseApp, firebaseMessaging] = await Promise.all([
        import(/* webpackIgnore: true */ FIREBASE_APP_SCRIPT),
        import(/* webpackIgnore: true */ FIREBASE_MESSAGING_SCRIPT),
      ]);

      this.firebaseMessagingModule = firebaseMessaging;
      const { initializeApp } = firebaseApp;
      (this as any).initializeApp = initializeApp;

      console.debug('[Firebase Service] Firebase scripts loaded successfully');
      this.scriptsLoaded = true;
    })();

    try {
      await this.scriptLoadingPromise;
    } catch (error) {
      console.error('[Firebase Service] Script loading failed:', error);
      this.scriptsLoaded = false;
      throw error;
    } finally {
      this.scriptLoadingPromise = null;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  private async unregisterLegacyServiceWorker(): Promise<void> {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      const currentUrl = window.location.href;
      const legacyRegistrations = registrations.filter(
        reg =>
          reg.active?.scriptURL?.includes(`service-worker.js`) && currentUrl.startsWith(reg.scope),
      );

      if (legacyRegistrations.length > 0) {
        await Promise.all(legacyRegistrations.map(reg => reg.unregister()));

        // Wait briefly to ensure the service worker is fully unregistered before proceeding.
        // For some browsers, the unregistration process may take a moment to complete, and attempting to register a new service worker immediately afterward can lead to conflicts.
        await new Promise(resolve => setTimeout(resolve, 1000));
        console.debug(
          `Legacy service worker(s) unregistered successfully (count: ${legacyRegistrations.length})`,
        );
      }
    } catch (error) {
      console.error('Failed to unregister legacy service worker:', error);
    }
  }

  public async registerFirebaseServiceWorker(): Promise<ServiceWorkerRegistration> {
    await this.unregisterLegacyServiceWorker();

    if (this.serviceWorkerRegistration) {
      console.debug(
        '[Firebase Service] Service worker already registered, returning cached registration',
      );
      return this.serviceWorkerRegistration;
    }

    if (!('serviceWorker' in navigator)) {
      console.error('[Firebase Service] Service workers not supported');
      throw new Error('Service workers are not supported in this browser');
    }

    const registration = await navigator.serviceWorker.register(
      isInStandaloneMode() ? SERVICE_WORKER_URL_PWA : SERVICE_WORKER_URL_NON_PWA,
      {
        scope: SERVICE_WORKER_SCOPE,
      },
    );

    const serviceWorker: ServiceWorker | null =
      registration.installing || registration.waiting || registration.active;

    if (!serviceWorker) {
      console.error('[Firebase Service] No service worker found in registration');
      throw new Error('No service worker found in registration');
    }

    // Wait for the service worker to become active using the registration
    if (serviceWorker.state !== 'activated') {
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      let onStateChange: (() => void) | undefined;

      const activationPromise = new Promise<void>((resolve, reject) => {
        timeoutId = setTimeout(() => {
          const error = new Error(
            `Service worker activation timed out. Current state: ${serviceWorker.state}`,
          );
          console.error('[Firebase Service]', error);
          reject(error);
        }, ACTIVATION_TIMEOUT);

        onStateChange = () => {
          console.debug(
            `[Firebase Service] Service worker state changed to: ${serviceWorker.state}`,
          );
          if (serviceWorker.state === 'activated') {
            resolve();
          } else if (serviceWorker.state === 'redundant') {
            reject(new Error('Service worker became redundant during activation'));
          }
        };

        serviceWorker.addEventListener('statechange', onStateChange);
      });

      try {
        await activationPromise;
      } finally {
        clearTimeout(timeoutId);
        if (onStateChange) {
          serviceWorker.removeEventListener('statechange', onStateChange);
        }
      }
    }

    console.debug('[Firebase Service] Service worker registered with scope:', registration.scope);

    this.serviceWorkerRegistration = registration;

    return registration;
  }

  // eslint-disable-next-line class-methods-use-this
  private parseConfig(webConfig: string): object {
    const cleanedString = webConfig
      .replace(/\\"/g, '"')
      .replace(
        /(?<pre_key_seperator>[{,]\s*)(?<key>\w+)(?<post_key_seperator>\s*:)/g,
        '$<pre_key_seperator>"$<key>"$<post_key_seperator>',
      );
    try {
      return JSON.parse(cleanedString);
    } catch (error) {
      console.error('[Firebase Service] Failed to parse config:', error);
      throw error;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  private displayNotificationAlert(payload: any): void {
    const notification = payload?.notification;
    const data = payload?.data;
    const title = notification?.title;
    const body = notification?.body;

    if (!title || !body) {
      console.debug('[Firebase Service] No title or body found in notification, skipping alert');
      return;
    }

    const link = payload?.fcmOptions?.link;
    const orderId = data?.orderId;

    const alertProps = new NotificationStickyAlertProps(
      NOTIFICATION_STICKY_ALERT_TYPE.NORMAL,
      title,
      {
        body,
        timeoutMs: NOTIFICATION_ALERT_TIMEOUT_MS,
        link,
        orderId,
      },
    );

    const alert = new StickyAlert(STICKY_ALERT_TYPE.NOTIFICATION, alertProps);
    addAlert(alert);
  }

  public async initializeFirebaseApp(webConfig: string): Promise<void> {
    await this.loadFirebaseScripts();

    if (!this.firebaseMessagingModule) {
      console.error('[Firebase Service] Firebase modules not loaded');
      throw new Error('Firebase modules not loaded');
    }

    if (this.firebaseApp && this.messaging) {
      console.debug('[Firebase Service] Firebase app already initialized');
      return;
    }

    const { initializeApp } = this as any;
    const { getMessaging, isSupported, onMessage } = this.firebaseMessagingModule;

    const configObject = this.parseConfig(webConfig);
    this.firebaseApp = initializeApp(configObject);
    console.debug('[Firebase Service] Firebase app initialized');
    const isFirebaseSupported = await isSupported();

    if (!isFirebaseSupported) {
      console.error('[Firebase Service] Firebase Messaging is not supported in this browser');
      throw new Error('Firebase Messaging is not supported in this browser');
    }

    this.messaging = getMessaging(this.firebaseApp);

    onMessage(this.messaging, (payload: any) => {
      console.debug('[Firebase Service] Message received:', payload);
      this.displayNotificationAlert(payload);
    });
  }

  private async getToken(
    serviceWorkerRegistration: ServiceWorkerRegistration,
    vapidKey: string,
  ): Promise<string> {
    if (!this.messaging) {
      console.error('[Firebase Service] Firebase Messaging is not initialized');
      throw new Error('Firebase Messaging is not initialized');
    }

    const { getToken } = this.firebaseMessagingModule;

    const token = await getToken(this.messaging, {
      serviceWorkerRegistration,
      vapidKey,
    });

    if (!token) {
      console.error('[Firebase Service] Failed to generate FCM token: token is empty');
      throw new Error('Failed to generate FCM token: token is empty');
    }

    return token;
  }

  private async deleteToken(): Promise<void> {
    if (!this.messaging) {
      console.error('[Firebase Service] Firebase Messaging is not initialized');
      throw new Error('Firebase Messaging is not initialized');
    }

    const { deleteToken } = this.firebaseMessagingModule;
    await deleteToken(this.messaging);
  }

  public async retrieveFirebaseMessagingToken(
    webConfig: string,
    vapidKey: string,
  ): Promise<string> {
    const serviceWorkerRegistration = await this.registerFirebaseServiceWorker();
    await this.initializeFirebaseApp(webConfig);
    const token = await this.getToken(serviceWorkerRegistration, vapidKey);
    console.debug('[Firebase Service] Retrieved FCM token:', token);

    return token;
  }

  public async deleteFirebaseMessagingToken(webConfig: string, vapidKey: string): Promise<void> {
    /*
     * Since we are using a custom service worker, we have to first fetch the token from FCM, and then delete it.
     * This is because the deleteToken does not take a serviceWorkerRegistration like getToken does.
     * Calling getToken ensures our custom service worker is tied to the messaging instance on the client.
     * There is an open issue with Firebase regarding this behavior - https://github.com/firebase/firebase-js-sdk/issues/8621
     * Once the above is resolved, we can simply call deleteToken without fetching the token first.
     */
    await this.initializeFirebaseApp(webConfig);
    await this.retrieveFirebaseMessagingToken(webConfig, vapidKey);
    await this.deleteToken();
    console.debug('[Firebase Service] Successfully unregistered FCM token');
  }
}

// Export singleton instance
export default FirebaseService.getInstance();
